'use strict';

/**
 * @fileoverview readingLanguage — the ONE rule for the language a person READS the apps in (PAYDAY-UAT-REMEDIATION-001
 * U8 W8-5; THINKPAD's ruling, 2026-11-01).
 *
 * THIS FILE IS THE IMPLEMENTATION; `lib/esm/readingLanguage.js` re-exports it. It is CommonJS because a SERVER needs it:
 * a server that speaks to a person on their behalf (Plaid Link, opened for them) needs the language they actually read,
 * not the preference they stored. A stored preference is kept as the member chose it (`./languageCode`), and an access
 * token's `language` claim is that preference, or null when none is stored. The language they READ is that preference
 * when a bundle ships for it, else the default: exactly what the client renders through its `fallbackLng`.
 *
 * The bundled languages and the default live HERE, once: the ESM constants re-export them and derive each language
 * entry's `hasBundle` from this list.
 *
 * @module @puffin-core/i18n-bootstrap/readingLanguage
 */

/** The languages the apps SHIP a bundle for (en-XA is the pseudo-locale QA renders in). */
const BUNDLED_LANGUAGE_CODES = Object.freeze(['en', 'es', 'en-XA']);

/** The language read when no other is: always a bundled one. */
const DEFAULT_LANGUAGE = 'en';

/**
 * The shipped language a tag resolves to: the tag itself when shipped, else its base language when shipped, else the
 * fallback. A tag is matched case-insensitively and `_` reads as `-` (`es_MX`, `ES` → `es`).
 *
 * @param {*} tag - a language tag, or anything a claim may carry
 * @param {ReadonlyArray<string>} shippedLanguages - FROZEN: an importer could otherwise widen it
 * @param {string} fallback - a shipped language
 * @returns {string}
 * @throws {TypeError} when the list is not frozen, or the fallback is not shipped (programmer errors)
 */
function resolveShippedLanguage(tag, shippedLanguages, fallback) {
  if (!Array.isArray(shippedLanguages) || !Object.isFrozen(shippedLanguages)) {
    throw new TypeError(
      'resolveShippedLanguage: shippedLanguages must be a frozen array of language codes',
    );
  }
  if (!shippedLanguages.includes(fallback)) {
    throw new TypeError(
      `resolveShippedLanguage: the fallback ${JSON.stringify(fallback)} is not a shipped language`,
    );
  }
  if (typeof tag !== 'string' || tag === '') return fallback;
  const wanted = tag.replace(/_/g, '-').toLowerCase();
  const exact = shippedLanguages.find((code) => code.toLowerCase() === wanted);
  if (exact) return exact;
  const base = wanted.split('-')[0];
  return shippedLanguages.find((code) => code.toLowerCase() === base) ?? fallback;
}

/**
 * The language a person reads the apps in, from their stored preference (a token's `language` claim, null when none).
 *
 * @param {*} preference
 * @returns {string} a bundled language
 */
function readingLanguageOf(preference) {
  return resolveShippedLanguage(preference, BUNDLED_LANGUAGE_CODES, DEFAULT_LANGUAGE);
}

module.exports = {
  BUNDLED_LANGUAGE_CODES,
  DEFAULT_LANGUAGE,
  resolveShippedLanguage,
  readingLanguageOf,
};
